import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import Avatar from '../../components/common/Avatar';
import MemberActivityReport from '../../components/reports/MemberActivityReport';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { 
  ArrowLeft,
  User,
  Save,
  Loader2,
  Mail,
  Phone,
  Building,
  Briefcase,
  Calendar,
  Euro,
  Clock,
  Send,
  Trash2,
  Shield,
  Link,
  ExternalLink,
  FileText,
  TrendingUp,
  CheckCircle2,
  AlertCircle,
  MapPin,
  CreditCard,
  FileSignature,
  Building2,
  ClipboardList,
  ScrollText,
  Plus
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/axios';

const MemberEditPage = () => {
  const navigate = useNavigate();
  const { memberId } = useParams();
  const { theme } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('info');
  
  // Reports and earnings data
  const [reports, setReports] = useState([]);
  const [loadingReports, setLoadingReports] = useState(false);
  
  // Evaluation stats
  const [evaluationStats, setEvaluationStats] = useState(null);
  const [loadingEvaluation, setLoadingEvaluation] = useState(false);
  
  // Contracts data
  const [contracts, setContracts] = useState([]);
  const [activeContract, setActiveContract] = useState(null);
  
  // Form data
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    role: 'team_member',
    job_title: '',
    department: '',
    hourly_rate: 0,
    daily_rate: 0,
    skills: '',
    bio: '',
    is_active: true,
    can_manage_projects: false,
    can_manage_clients: false,
    can_manage_finances: false,
    google_calendar_url: '',
    google_calendar_public_url: '',
    // Informations légales pour les contrats
    legal_status: '', // auto-entrepreneur, sasu, eurl, sarl, etc.
    siret: '',
    iban: '',
    bic: '',
    address: '',
    city: '',
    postal_code: '',
    country: 'France'
  });
  
  // Contract state
  const [memberContract, setMemberContract] = useState(null);
  const [loadingContract, setLoadingContract] = useState(false);

  useEffect(() => {
    if (memberId) {
      fetchMember();
      fetchMemberReports();
      fetchMemberContract();
      fetchEvaluationStats();
    }
  }, [memberId]);

  const fetchEvaluationStats = async () => {
    try {
      setLoadingEvaluation(true);
      const response = await api.get(`/ratings/member/${memberId}/evaluation-stats`);
      setEvaluationStats(response.data);
    } catch (error) {
      console.error('Error fetching evaluation stats:', error);
    } finally {
      setLoadingEvaluation(false);
    }
  };

  const fetchMemberContract = async () => {
    try {
      setLoadingContract(true);
      const response = await api.get(`/member-contracts/?member_id=${memberId}`);
      const contracts = response.data || [];
      // Get the most recent active or pending contract
      const activeContract = contracts.find(c => ['active', 'pending_member', 'pending_agency'].includes(c.status));
      setMemberContract(activeContract || null);
      setActiveContract(activeContract || null);
      setContracts(contracts);
    } catch (error) {
      console.error('Error fetching member contract:', error);
    } finally {
      setLoadingContract(false);
    }
  };

  const fetchMember = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/users/${memberId}`);
      const member = response.data;
      setFormData({
        first_name: member.first_name || '',
        last_name: member.last_name || '',
        email: member.email || '',
        phone: member.phone || '',
        role: member.role || 'team_member',
        job_title: member.job_title || '',
        department: member.department || '',
        hourly_rate: member.hourly_rate || 0,
        daily_rate: member.daily_rate || 0,
        skills: member.skills?.join(', ') || '',
        bio: member.bio || '',
        is_active: member.is_active !== false,
        can_manage_projects: member.permissions?.can_manage_projects || false,
        can_manage_clients: member.permissions?.can_manage_clients || false,
        can_manage_finances: member.permissions?.can_manage_finances || false,
        google_calendar_url: member.google_calendar_url || '',
        google_calendar_public_url: member.google_calendar_public_url || '',
        // Informations légales
        legal_status: member.legal_status || '',
        siret: member.siret || '',
        iban: member.iban || '',
        bic: member.bic || '',
        address: member.address || '',
        city: member.city || '',
        postal_code: member.postal_code || '',
        country: member.country || 'France'
      });
    } catch (error) {
      console.error('Error fetching member:', error);
      toast.error('Erreur lors du chargement du membre');
      navigate('/users');
    } finally {
      setLoading(false);
    }
  };

  const fetchMemberReports = async () => {
    try {
      setLoadingReports(true);
      const response = await api.get(`/monthly-reports/?member_id=${memberId}`);
      setReports(response.data || []);
    } catch (error) {
      console.error('Error fetching member reports:', error);
    } finally {
      setLoadingReports(false);
    }
  };

  // Calculate reports stats
  const reportStats = {
    totalReports: reports.length,
    totalEarned: reports.filter(r => r.status === 'paid').reduce((acc, r) => acc + (r.total_amount || 0), 0),
    totalHours: reports.reduce((acc, r) => acc + (r.total_hours || 0), 0),
    pendingValidation: reports.filter(r => r.status === 'pending_validation').length,
    pendingPayment: reports.filter(r => ['validated', 'pending_invoice', 'pending_payment'].includes(r.status)).length
  };

  const handleSave = async () => {
    if (!formData.first_name || !formData.last_name || !formData.email) {
      toast.error('Veuillez remplir les champs obligatoires');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        skills: formData.skills ? formData.skills.split(',').map(s => s.trim()).filter(Boolean) : [],
        permissions: {
          can_manage_projects: formData.can_manage_projects,
          can_manage_clients: formData.can_manage_clients,
          can_manage_finances: formData.can_manage_finances
        },
        google_calendar_url: formData.google_calendar_url,
        google_calendar_public_url: formData.google_calendar_public_url,
        // Informations légales
        legal_status: formData.legal_status,
        siret: formData.siret,
        iban: formData.iban,
        bic: formData.bic,
        address: formData.address,
        city: formData.city,
        postal_code: formData.postal_code,
        country: formData.country
      };

      await api.put(`/users/${memberId}`, payload);
      toast.success('Membre mis à jour avec succès');
      navigate('/users');
    } catch (error) {
      console.error('Error saving member:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleSendCredentials = async () => {
    try {
      await api.post(`/users/${memberId}/send-credentials`);
      toast.success(`Identifiants envoyés à ${formData.email}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer ${formData.first_name} ${formData.last_name} ?\n\nCette action est irréversible.`)) return;
    
    try {
      await api.delete(`/users/${memberId}`);
      toast.success('Membre supprimé');
      navigate('/users');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const roleLabels = {
    agency: 'Administrateur',
    project_manager: 'Chef de projet',
    team_member: 'Membre d\'équipe',
    creative: 'Créatif'
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />
      <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
      
      <main className={cn(
        "transition-all duration-300 px-4 md:px-6 pb-6",
        collapsed ? "md:ml-20" : "md:ml-64"
      )} style={{ paddingTop: "130px" }}>
        
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/users')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold" style={{ color: theme.colors?.primary }}>
                Modifier le membre
              </h1>
              <p className="text-slate-500 text-sm">
                {formData.first_name} {formData.last_name}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={handleSendCredentials}>
              <Send className="w-4 h-4 mr-2" />
              Envoyer identifiants
            </Button>
            <Button 
              variant="outline" 
              className="text-red-600 hover:bg-red-50"
              onClick={handleDelete}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Supprimer
            </Button>
            <Button 
              onClick={handleSave}
              disabled={saving}
              style={{ backgroundColor: theme.colors?.accent }}
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Enregistrer
            </Button>
          </div>
        </div>

        {/* Member Summary Card */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex items-center gap-6">
              <Avatar name={`${formData.first_name} ${formData.last_name}`} size="lg" />
              <div className="flex-1">
                <h2 className="text-xl font-bold">{formData.first_name} {formData.last_name}</h2>
                <div className="flex items-center gap-3 mt-1">
                  <Badge variant="outline">
                    {roleLabels[formData.role] || formData.role}
                  </Badge>
                  {formData.job_title && (
                    <span className="text-sm text-slate-500">{formData.job_title}</span>
                  )}
                  <Badge variant={formData.is_active ? "default" : "secondary"} className={formData.is_active ? "bg-emerald-500" : ""}>
                    {formData.is_active ? "Actif" : "Inactif"}
                  </Badge>
                </div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold" style={{ color: theme.colors?.accent }}>
                  {reportStats.totalEarned.toLocaleString('fr-FR')}€
                </div>
                <p className="text-sm text-slate-500">Total perçu</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-4 mb-6">
            <TabsTrigger value="info" className="flex items-center gap-2">
              <User className="w-4 h-4" />
              Profil
            </TabsTrigger>
            <TabsTrigger value="contracts" className="flex items-center gap-2">
              <FileSignature className="w-4 h-4" />
              Contrats
              {contracts.length > 0 && (
                <Badge variant="secondary" className="ml-1 text-xs">{contracts.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="reports" className="flex items-center gap-2">
              <ClipboardList className="w-4 h-4" />
              Rapports
              {reportStats.pendingValidation > 0 && (
                <Badge className="ml-1 text-xs bg-amber-500">{reportStats.pendingValidation}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="evaluation" className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />
              Évaluation
            </TabsTrigger>
          </TabsList>

          {/* TAB: Profil */}
          <TabsContent value="info">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-6">
                {/* Personal Info */}
                <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <User className="w-5 h-5" />
                  Informations personnelles
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Prénom *</Label>
                    <Input
                      value={formData.first_name}
                      onChange={(e) => updateField('first_name', e.target.value)}
                      placeholder="Prénom"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Nom *</Label>
                    <Input
                      value={formData.last_name}
                      onChange={(e) => updateField('last_name', e.target.value)}
                      placeholder="Nom"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => updateField('email', e.target.value)}
                      placeholder="email@example.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Téléphone</Label>
                    <Input
                      value={formData.phone}
                      onChange={(e) => updateField('phone', e.target.value)}
                      placeholder="+33 6 00 00 00 00"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Professional Info */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Briefcase className="w-5 h-5" />
                  Informations professionnelles
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Rôle</Label>
                    <Select value={formData.role} onValueChange={(v) => updateField('role', v)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="project_manager">Chef de projet</SelectItem>
                        <SelectItem value="team_member">Membre équipe</SelectItem>
                        <SelectItem value="creative">Créatif</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Poste / Titre</Label>
                    <Input
                      value={formData.job_title}
                      onChange={(e) => updateField('job_title', e.target.value)}
                      placeholder="Ex: Développeur web"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Département</Label>
                  <Input
                    value={formData.department}
                    onChange={(e) => updateField('department', e.target.value)}
                    placeholder="Ex: Tech, Design, Marketing"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Taux horaire (€)</Label>
                    <Input
                      type="number"
                      value={formData.hourly_rate}
                      onChange={(e) => updateField('hourly_rate', parseFloat(e.target.value) || 0)}
                      placeholder="0"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Taux journalier (€)</Label>
                    <Input
                      type="number"
                      value={formData.daily_rate}
                      onChange={(e) => updateField('daily_rate', parseFloat(e.target.value) || 0)}
                      placeholder="0"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Compétences (séparées par des virgules)</Label>
                  <Input
                    value={formData.skills}
                    onChange={(e) => updateField('skills', e.target.value)}
                    placeholder="Ex: React, Node.js, Design UI/UX"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Bio</Label>
                  <Textarea
                    value={formData.bio}
                    onChange={(e) => updateField('bio', e.target.value)}
                    placeholder="Présentation courte..."
                    rows={3}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Legal Information - For contracts */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Building2 className="w-5 h-5" />
                  Informations légales
                </CardTitle>
                <CardDescription>
                  Requis pour générer les contrats de prestation
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Statut juridique</Label>
                    <Select value={formData.legal_status} onValueChange={(v) => updateField('legal_status', v)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionner..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="auto_entrepreneur">Auto-entrepreneur</SelectItem>
                        <SelectItem value="micro_entreprise">Micro-entreprise</SelectItem>
                        <SelectItem value="eurl">EURL</SelectItem>
                        <SelectItem value="sasu">SASU</SelectItem>
                        <SelectItem value="sarl">SARL</SelectItem>
                        <SelectItem value="sas">SAS</SelectItem>
                        <SelectItem value="ei">Entreprise Individuelle</SelectItem>
                        <SelectItem value="portage">Portage salarial</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>N° SIRET</Label>
                    <Input
                      value={formData.siret}
                      onChange={(e) => updateField('siret', e.target.value)}
                      placeholder="123 456 789 00012"
                      maxLength={17}
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    Adresse
                  </Label>
                  <Input
                    value={formData.address}
                    onChange={(e) => updateField('address', e.target.value)}
                    placeholder="123 rue de la Paix"
                  />
                </div>
                
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Code postal</Label>
                    <Input
                      value={formData.postal_code}
                      onChange={(e) => updateField('postal_code', e.target.value)}
                      placeholder="75001"
                      maxLength={5}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Ville</Label>
                    <Input
                      value={formData.city}
                      onChange={(e) => updateField('city', e.target.value)}
                      placeholder="Paris"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Pays</Label>
                    <Input
                      value={formData.country}
                      onChange={(e) => updateField('country', e.target.value)}
                      placeholder="France"
                    />
                  </div>
                </div>
                
                <div className="pt-2 border-t space-y-4">
                  <Label className="flex items-center gap-2">
                    <CreditCard className="w-4 h-4" />
                    Coordonnées bancaires (RIB)
                  </Label>
                  <div className="space-y-2">
                    <Label className="text-xs text-slate-500">IBAN</Label>
                    <Input
                      value={formData.iban}
                      onChange={(e) => updateField('iban', e.target.value.toUpperCase())}
                      placeholder="FR76 1234 5678 9012 3456 7890 123"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs text-slate-500">BIC/SWIFT</Label>
                    <Input
                      value={formData.bic}
                      onChange={(e) => updateField('bic', e.target.value.toUpperCase())}
                      placeholder="BNPAFRPP"
                      maxLength={11}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Status */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Statut</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-slate-500" />
                    <span className="text-sm">Compte actif</span>
                  </div>
                  <Switch
                    checked={formData.is_active}
                    onCheckedChange={(v) => updateField('is_active', v)}
                  />
                </div>
                {!formData.is_active && (
                  <p className="text-xs text-amber-600 mt-2">
                    Ce membre ne pourra plus se connecter
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Member Contract */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <FileSignature className="w-5 h-5" />
                  Contrat de prestation
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loadingContract ? (
                  <div className="flex justify-center py-4">
                    <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                  </div>
                ) : memberContract ? (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-600">Statut</span>
                      <Badge className={
                        memberContract.status === 'active' ? 'bg-green-100 text-green-700' :
                        memberContract.status === 'pending_member' ? 'bg-amber-100 text-amber-700' :
                        memberContract.status === 'pending_agency' ? 'bg-blue-100 text-blue-700' :
                        'bg-slate-100 text-slate-700'
                      }>
                        {memberContract.status === 'active' && 'Actif'}
                        {memberContract.status === 'pending_member' && 'Attente signature membre'}
                        {memberContract.status === 'pending_agency' && 'Attente validation agence'}
                        {memberContract.status === 'terminated' && 'Résilié'}
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-600">Type</span>
                      <span className="font-medium">
                        {memberContract.contract_type === 'monthly' ? 'Mensuel' : 'Mission'}
                      </span>
                    </div>
                    {memberContract.monthly_rate && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-600">Tarif mensuel</span>
                        <span className="font-medium">{memberContract.monthly_rate.toLocaleString('fr-FR')}€</span>
                      </div>
                    )}
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="w-full mt-2"
                      onClick={() => navigate(`/member-contracts/${memberContract.id}`)}
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      Voir le contrat
                    </Button>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <FileSignature className="w-10 h-10 mx-auto text-slate-300 mb-3" />
                    <p className="text-sm text-slate-500 mb-3">
                      Aucun contrat actif
                    </p>
                    {formData.siret ? (
                      <Button 
                        size="sm" 
                        className="w-full"
                        style={{ backgroundColor: theme.colors?.accent }}
                        onClick={() => navigate(`/member-contracts/new?member_id=${memberId}`)}
                      >
                        <FileSignature className="w-4 h-4 mr-2" />
                        Créer un contrat
                      </Button>
                    ) : (
                      <p className="text-xs text-amber-600">
                        Renseignez le SIRET pour créer un contrat
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Permissions */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Shield className="w-5 h-5" />
                  Permissions
                </CardTitle>
                <CardDescription>
                  Autorisations spéciales pour ce membre
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">Gérer les projets</p>
                    <p className="text-xs text-slate-500">Créer, modifier, supprimer</p>
                  </div>
                  <Switch
                    checked={formData.can_manage_projects}
                    onCheckedChange={(v) => updateField('can_manage_projects', v)}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">Gérer les clients</p>
                    <p className="text-xs text-slate-500">Accès aux données clients</p>
                  </div>
                  <Switch
                    checked={formData.can_manage_clients}
                    onCheckedChange={(v) => updateField('can_manage_clients', v)}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">Voir les finances</p>
                    <p className="text-xs text-slate-500">Accès aux données financières</p>
                  </div>
                  <Switch
                    checked={formData.can_manage_finances}
                    onCheckedChange={(v) => updateField('can_manage_finances', v)}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Google Calendar */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Calendar className="w-5 h-5" />
                  Agenda Google
                </CardTitle>
                <CardDescription>
                  Liens vers le calendrier Google du membre
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Link className="w-4 h-4" />
                    URL Embed (iframe)
                  </Label>
                  <Input
                    value={formData.google_calendar_url}
                    onChange={(e) => updateField('google_calendar_url', e.target.value)}
                    placeholder="https://calendar.google.com/calendar/embed?src=..."
                  />
                  <p className="text-xs text-slate-500">
                    URL pour intégrer le calendrier dans application
                  </p>
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <ExternalLink className="w-4 h-4" />
                    URL Publique (prise de RDV)
                  </Label>
                  <Input
                    value={formData.google_calendar_public_url}
                    onChange={(e) => updateField('google_calendar_public_url', e.target.value)}
                    placeholder="https://calendar.google.com/calendar/appointments/..."
                  />
                  <p className="text-xs text-slate-500">
                    Lien de prise de RDV partagé avec les clients
                  </p>
                </div>
                {formData.google_calendar_public_url && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="w-full"
                    onClick={() => window.open(formData.google_calendar_public_url, '_blank')}
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Tester le lien de RDV
                  </Button>
                )}
              </CardContent>
            </Card>
              </div>
            </div>
          </TabsContent>

          {/* TAB: Contrats */}
          <TabsContent value="contracts">
            <div className="space-y-6">
              {/* Active Contract Summary */}
              {activeContract && (
                <Card className="border-emerald-200 bg-emerald-50/30">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="flex items-center gap-2 text-base">
                        <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                        Contrat actif
                      </CardTitle>
                      <Badge className="bg-emerald-500">Actif</Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <p className="text-sm text-slate-500">Taux horaire</p>
                        <p className="text-xl font-bold">{activeContract.hourly_rate}€/h</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Date de début</p>
                        <p className="font-medium">{new Date(activeContract.start_date).toLocaleDateString('fr-FR')}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Type</p>
                        <p className="font-medium">{activeContract.contract_type === 'monthly' ? 'Récurrent' : 'Mission'}</p>
                      </div>
                    </div>
                    <Button 
                      className="mt-4 w-full"
                      variant="outline"
                      onClick={() => navigate(`/member-contracts`)}
                    >
                      Voir le détail du contrat
                    </Button>
                  </CardContent>
                </Card>
              )}

              {/* All Contracts */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <ScrollText className="w-5 h-5" style={{ color: theme.colors?.accent }} />
                        Historique des contrats
                      </CardTitle>
                      <CardDescription>
                        Tous les contrats du membre : actifs, en attente et passés
                      </CardDescription>
                    </div>
                    <Button onClick={() => navigate(`/member-contracts/new?member_id=${memberId}`)}>
                      <Plus className="w-4 h-4 mr-2" />
                      Nouveau contrat
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {contracts.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <FileSignature className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                      <p>Aucun contrat pour ce membre</p>
                      <Button 
                        variant="outline" 
                        className="mt-3"
                        onClick={() => navigate(`/member-contracts/new?member_id=${memberId}`)}
                      >
                        Créer un contrat
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {contracts.map((contract) => (
                        <div 
                          key={contract.id}
                          className={cn(
                            "p-4 border rounded-lg cursor-pointer hover:bg-slate-50 transition-colors",
                            contract.status === 'active' && "border-emerald-200 bg-emerald-50/30",
                            contract.status === 'pending_member' && "border-amber-200 bg-amber-50/30",
                            contract.status === 'pending_agency' && "border-blue-200 bg-blue-50/30",
                            contract.status === 'terminated' && "border-slate-200 bg-slate-50/50 opacity-75"
                          )}
                          onClick={() => navigate(`/member-contracts`)}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-medium">{contract.contract_type === 'monthly' ? 'Contrat récurrent' : 'Contrat de mission'}</span>
                                <Badge variant={
                                  contract.status === 'active' ? 'default' :
                                  contract.status === 'terminated' ? 'secondary' : 'outline'
                                } className={cn(
                                  contract.status === 'active' && "bg-emerald-500",
                                  contract.status === 'pending_member' && "border-amber-400 text-amber-700",
                                  contract.status === 'pending_agency' && "border-blue-400 text-blue-700"
                                )}>
                                  {contract.status === 'active' ? 'Actif' :
                                   contract.status === 'pending_member' ? 'En attente membre' :
                                   contract.status === 'pending_agency' ? 'En attente agence' :
                                   contract.status === 'terminated' ? 'Terminé' : contract.status}
                                </Badge>
                              </div>
                              <div className="text-sm text-slate-500 mt-1">
                                Du {new Date(contract.start_date).toLocaleDateString('fr-FR')}
                                {contract.end_date && ` au ${new Date(contract.end_date).toLocaleDateString('fr-FR')}`}
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="font-semibold text-lg">{contract.hourly_rate}€/h</div>
                              {contract.monthly_hours && (
                                <div className="text-sm text-slate-500">{contract.monthly_hours}h/mois</div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* TAB: Rapports */}
          <TabsContent value="reports">
            <div className="space-y-6">
              {/* Reports Summary */}
              <div className="grid grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <p className="text-3xl font-bold">{reportStats.totalReports}</p>
                      <p className="text-sm text-slate-500">Total rapports</p>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <p className="text-3xl font-bold">{reportStats.totalHours.toFixed(1)}h</p>
                      <p className="text-sm text-slate-500">Heures totales</p>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-amber-200">
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <p className="text-3xl font-bold text-amber-600">{reportStats.pendingValidation}</p>
                      <p className="text-sm text-slate-500">En attente validation</p>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-emerald-200">
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <p className="text-3xl font-bold text-emerald-600">{reportStats.totalEarned.toLocaleString('fr-FR')}€</p>
                      <p className="text-sm text-slate-500">Total payé</p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Activity Reports Component */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <ClipboardList className="w-5 h-5" style={{ color: theme.colors?.accent }} />
                    Rapports d&apos;activité détaillés
                  </CardTitle>
                  <CardDescription>
                    Suivi des heures, tâches et performances pour validation des règlements
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <MemberActivityReport 
                    memberId={memberId}
                    contractId={activeContract?.id}
                    onReportValidated={fetchMemberReports}
                  />
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* TAB: Évaluation / Bonus-Malus */}
          <TabsContent value="evaluation">
            {loadingEvaluation ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin" style={{ color: theme.colors?.primary }} />
              </div>
            ) : (
            <div className="space-y-6">
              {/* Performance Overview */}
              <div className="grid grid-cols-3 gap-6">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-emerald-600" />
                      Performance globale
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-center">
                      <div className="text-4xl font-bold text-emerald-600">
                        {evaluationStats?.performance?.grade || '-'}
                      </div>
                      <p className="text-sm text-slate-500 mt-2">
                        {evaluationStats?.performance?.grade_label || 'Pas d\'évaluation'}
                      </p>
                      {evaluationStats?.performance?.total_ratings > 0 && (
                        <div className="mt-4 space-y-2">
                          <div className="flex justify-between text-sm">
                            <span>Qualité</span>
                            <span className="font-medium">{evaluationStats?.performance?.avg_quality || 0}/10</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span>Délais</span>
                            <span className="font-medium">{evaluationStats?.performance?.avg_deadline || 0}/10</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span>Communication</span>
                            <span className="font-medium">{evaluationStats?.performance?.avg_communication || 0}/10</span>
                          </div>
                          <p className="text-xs text-slate-400 mt-2">
                            Basé sur {evaluationStats?.performance?.total_ratings} évaluation(s)
                          </p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Euro className="w-5 h-5 text-blue-600" />
                      Bonus accumulés
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-center">
                      <div className="text-4xl font-bold text-blue-600">
                        +{(evaluationStats?.bonuses?.total_bonus || 0).toLocaleString('fr-FR')}€
                      </div>
                      <p className="text-sm text-slate-500 mt-2">Ce trimestre</p>
                    </div>
                    {evaluationStats?.bonuses?.bonus_items?.length > 0 ? (
                      <div className="mt-4 space-y-2">
                        {evaluationStats.bonuses.bonus_items.map((bonus, idx) => (
                          <div key={idx} className="p-2 bg-emerald-50 rounded text-sm">
                            <div className="flex justify-between">
                              <span className="truncate max-w-[150px]">{bonus.reason || 'Bonus'}</span>
                              <span className="font-medium text-emerald-600">+{bonus.bonus_amount}€</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-slate-400 text-center mt-4">Aucun bonus ce trimestre</p>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <AlertCircle className="w-5 h-5 text-orange-600" />
                      Malus appliqués
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-center">
                      <div className="text-4xl font-bold text-orange-600">
                        -{(evaluationStats?.bonuses?.total_malus || 0).toLocaleString('fr-FR')}€
                      </div>
                      <p className="text-sm text-slate-500 mt-2">Ce trimestre</p>
                    </div>
                    {evaluationStats?.bonuses?.malus_items?.length > 0 ? (
                      <div className="mt-4 space-y-2">
                        {evaluationStats.bonuses.malus_items.map((malus, idx) => (
                          <div key={idx} className="p-2 bg-orange-50 rounded text-sm">
                            <div className="flex justify-between">
                              <span className="truncate max-w-[150px]">{malus.reason || 'Malus'}</span>
                              <span className="font-medium text-orange-600">-{Math.abs(malus.bonus_amount)}€</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-slate-400 text-center mt-4">Aucun malus ce trimestre</p>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Net Balance */}
              {evaluationStats?.bonuses && (
                <Card className={evaluationStats.bonuses.net >= 0 ? 'border-emerald-200 bg-emerald-50/50' : 'border-orange-200 bg-orange-50/50'}>
                  <CardContent className="py-4">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">Solde net bonus/malus ce trimestre</span>
                      <span className={`text-2xl font-bold ${evaluationStats.bonuses.net >= 0 ? 'text-emerald-600' : 'text-orange-600'}`}>
                        {evaluationStats.bonuses.net >= 0 ? '+' : ''}{evaluationStats.bonuses.net.toLocaleString('fr-FR')}€
                      </span>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Evaluations by Client */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <User className="w-5 h-5" style={{ color: theme.colors?.accent }} />
                    Évaluations par client
                  </CardTitle>
                  <CardDescription>
                    Retours des clients sur les projets réalisés
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {evaluationStats?.ratings?.length > 0 ? (
                      evaluationStats.ratings.map((rating, idx) => (
                        <div key={idx} className="p-4 border rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-3">
                              <Avatar name={rating.client_name || 'Client'} size="sm" />
                              <div>
                                <p className="font-medium">{rating.client_name || 'Client'}</p>
                                <p className="text-sm text-slate-500">{rating.project_name || 'Projet'}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-1">
                              {[1,2,3,4,5].map(i => (
                                <svg key={i} className={`w-5 h-5 ${i <= Math.round(rating.overall_rating || 0) ? 'text-amber-400' : 'text-slate-200'}`} fill="currentColor" viewBox="0 0 20 20">
                                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                </svg>
                              ))}
                              <span className="ml-2 font-medium">{rating.overall_rating || 0}/5</span>
                            </div>
                          </div>
                          {rating.comment && (
                            <p className="text-sm text-slate-600 italic">&quot;{rating.comment}&quot;</p>
                          )}
                          <p className="text-xs text-slate-400 mt-2">
                            Évalué le {new Date(rating.created_at).toLocaleDateString('fr-FR')}
                          </p>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-8 text-slate-400">
                        <TrendingUp className="w-12 h-12 mx-auto mb-3 opacity-30" />
                        <p className="text-sm">Aucune évaluation client pour le moment</p>
                        <p className="text-xs mt-1">Les évaluations apparaîtront ici après validation des projets</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Budget vs Consumed */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Clock className="w-5 h-5" style={{ color: theme.colors?.accent }} />
                    Budget temps vs Consommé
                  </CardTitle>
                  <CardDescription>
                    Comparaison entre le temps budgété et le temps réellement consommé
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {evaluationStats?.time_budget ? (
                    <div className="space-y-4">
                      <div className="grid grid-cols-3 gap-4">
                        <div className="text-center p-4 bg-slate-50 rounded-lg">
                          <p className="text-2xl font-bold" style={{ color: theme.colors?.primary }}>
                            {evaluationStats.time_budget.budgeted_hours}h
                          </p>
                          <p className="text-sm text-slate-500">Budgété</p>
                        </div>
                        <div className="text-center p-4 bg-slate-50 rounded-lg">
                          <p className="text-2xl font-bold text-blue-600">
                            {evaluationStats.time_budget.consumed_hours}h
                          </p>
                          <p className="text-sm text-slate-500">Consommé</p>
                        </div>
                        <div className="text-center p-4 bg-slate-50 rounded-lg">
                          <p className={`text-2xl font-bold ${evaluationStats.time_budget.efficiency >= 100 ? 'text-emerald-600' : 'text-orange-600'}`}>
                            {evaluationStats.time_budget.efficiency}%
                          </p>
                          <p className="text-sm text-slate-500">Efficacité</p>
                        </div>
                      </div>
                      
                      {/* Progress bar */}
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span>Utilisation du budget</span>
                          <span className="font-medium">
                            {evaluationStats.time_budget.budgeted_hours > 0 
                              ? Math.round((evaluationStats.time_budget.consumed_hours / evaluationStats.time_budget.budgeted_hours) * 100)
                              : 0}%
                          </span>
                        </div>
                        <div className="h-3 bg-slate-200 rounded-full overflow-hidden">
                          <div 
                            className={`h-full rounded-full transition-all ${
                              evaluationStats.time_budget.consumed_hours <= evaluationStats.time_budget.budgeted_hours 
                                ? 'bg-emerald-500' 
                                : 'bg-orange-500'
                            }`}
                            style={{ 
                              width: `${Math.min(100, evaluationStats.time_budget.budgeted_hours > 0 
                                ? (evaluationStats.time_budget.consumed_hours / evaluationStats.time_budget.budgeted_hours) * 100 
                                : 0)}%` 
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-slate-400">
                      <Clock className="w-12 h-12 mx-auto mb-3 opacity-30" />
                      <p className="text-sm">Aucune donnée de temps disponible</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default MemberEditPage;
